import { MapPin, Send } from "lucide-react";
import { useState, type KeyboardEvent } from "react";

import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { showToast } from "@/components/ui/toast";

interface MessageComposerProps {
  onSend: (text: string) => void;
  onTyping: () => void;
  isDisabled?: boolean;
  disabledReason?: string;
}

const GEOLOCATION_ERROR_MESSAGE: Record<number, string> = {
  1: "Location permission was denied.",
  2: "Your location couldn't be determined.",
  3: "Getting your location took too long. Try again.",
};

/**
 * docs/ui/component-inventory.md's `MessageComposer` — text-only variant.
 * The attachment affordance and inline "Make an offer" `TransactionCTA`
 * trigger are real variants that doc describes, but nothing elsewhere in
 * this codebase has a file-upload-to-chat or a structured-offer domain
 * concept to back them — building either now would be inventing behavior,
 * not implementing a decided one. `Enter` sends, `Shift+Enter` inserts a
 * newline, matching the doc's own note for both platforms.
 *
 * The location button is the one exception, added on direct request: it
 * reads the browser's Geolocation API (a permission prompt, not silent) and
 * sends a Google Maps link as a plain message — no new attachment/message-
 * type concept needed, it rides the exact same `onSend(text)` path as any
 * other message. `ChatBubble` linkifies the URL so it's actually clickable.
 */
export function MessageComposer({ onSend, onTyping, isDisabled, disabledReason }: MessageComposerProps) {
  const [value, setValue] = useState("");
  const [isLocating, setIsLocating] = useState(false);

  function handleSend() {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSend(trimmed);
    setValue("");
  }

  function handleKeyDown(event: KeyboardEvent<HTMLTextAreaElement>) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSend();
    }
  }

  function handleShareLocation() {
    if (!navigator.geolocation) {
      showToast.error("Location isn't supported on this browser.");
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setIsLocating(false);
        const { latitude, longitude } = position.coords;
        const mapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;
        onSend(`📍 My current location: ${mapsUrl}`);
      },
      (error) => {
        setIsLocating(false);
        showToast.error(GEOLOCATION_ERROR_MESSAGE[error.code] ?? "Couldn't get your location.");
      },
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  }

  if (isDisabled) {
    return (
      <div className="border-t border-border px-4 py-3 text-body-sm text-muted-foreground">
        {disabledReason ?? "You can't send messages in this conversation."}
      </div>
    );
  }

  return (
    <div className="flex items-end gap-2 border-t border-border p-3">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label="Share current location"
            onClick={handleShareLocation}
            isLoading={isLocating}
          >
            <MapPin className="h-4 w-4" aria-hidden="true" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Share location</TooltipContent>
      </Tooltip>
      <Textarea
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
          onTyping();
        }}
        onKeyDown={handleKeyDown}
        placeholder="Type a message…"
        rows={1}
        className="min-h-10 flex-1 resize-none"
      />
      <Button type="button" size="icon" aria-label="Send message" onClick={handleSend}>
        <Send className="h-4 w-4" aria-hidden="true" />
      </Button>
    </div>
  );
}
